import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { editExceptionRule, getPaycodesDurationType } from 'utils/ExceptionRule';
import { normalizeString } from 'utils/Validations';
import {
    ExceptionRuleFormDialog,
    type ExceptionRuleFormValues,
    type PaycodeOption,
} from './ExceptionRuleFormDialog';

type ExceptionRuleRow = {
    id: number | string;
    name: string;
    description: string;
    no_planning: boolean;
    long_interval: string;
    shortened_shift: string;
    punch_in: string[];
    punch_out: string[];
    in_paycode_id: number | string | null;
    out_paycode_id: number | string | null;
};

type EditExceptionRuleProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    selectedRow: Array<number | string>;
    rows: ExceptionRuleRow[];
    reloading: () => void;
    setSelectedRow: (rows: Array<number | string>) => void;
};

const emptyValues: ExceptionRuleFormValues = {
    name: '',
    description: '',
    not_planning: false,
    long_interval: '',
    shortened_shift: '',
    in_punch_very_early: '',
    in_punch_early: '',
    in_punch_late: '',
    out_punch_early: '',
    out_punch_late: '',
    out_punch_very_late: '',
};

const EditExceptionRule = ({ open, setOpen, selectedRow, rows, reloading, setSelectedRow }: EditExceptionRuleProps) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(false);
    const [values, setValues] = useState<ExceptionRuleFormValues>(emptyValues);
    const [paycodes, setPaycodes] = useState<any[]>([]);
    const [inPaycodesDuration, setInPaycodesDuration] = useState<any | null>(null);
    const [outPaycodesDuration, setOutPaycodesDuration] = useState<any | null>(null);

    useEffect(() => {
        const fetchExceptionRule = async () => {
            try {
                if (!open || selectedRow.length === 0) return;

                const response = await getPaycodesDurationType(setLoading, 'exception_rule');
                const row = rows.find((item) => item.id === selectedRow[0]);
                if (!row) return;

                setPaycodes(response || []);

                setValues({
                    name: row.name,
                    description: row.description,
                    not_planning: row.no_planning,
                    long_interval: row.long_interval,
                    shortened_shift: row.shortened_shift,
                    in_punch_very_early: row.punch_in?.[0] || '',
                    in_punch_early: row.punch_in?.[1] || '',
                    in_punch_late: row.punch_in?.[2] || '',
                    out_punch_early: row.punch_out?.[0] || '',
                    out_punch_late: row.punch_out?.[1] || '',
                    out_punch_very_late: row.punch_out?.[2] || '',
                });

                setInPaycodesDuration((response || []).find((paycode: any) => paycode.paycode_id === row.in_paycode_id) || null);
                setOutPaycodesDuration((response || []).find((paycode: any) => paycode.paycode_id === row.out_paycode_id) || null);
                setLoading(false);
            } catch (error) {
                console.error('Error:', error);
            }
        };

        fetchExceptionRule();
    }, [open, selectedRow, rows]);

    const closeForm = () => {
        setValues(emptyValues);
        setInPaycodesDuration(null);
        setOutPaycodesDuration(null);
        setLoading(false);
        setOpen(false);
        setAlert(false);
        setSelectedRow([]);
    };

    const existName = (name: string) => {
        const normalizedName = normalizeString(name);
        const selected = rows.find((row) => row.id === selectedRow[0]);

        if (selected && normalizeString(selected.name) === normalizedName) {
            return false;
        }

        return rows.some((row) => normalizeString(row.name) === normalizedName);
    };

    const validateInPunchVeryEarly = (value: string) => {
        if (value.length === 5 && values.in_punch_very_early) {
            const inPunchEarly = value.split(':');
            const inPunchVeryEarly = values.in_punch_very_early.split(':');

            const inPunchEarlyHours = parseInt(inPunchEarly[0], 10);
            const inPunchEarlyMinutes = parseInt(inPunchEarly[1], 10);
            const inPunchVeryEarlyHours = parseInt(inPunchVeryEarly[0], 10);
            const inPunchVeryEarlyMinutes = parseInt(inPunchVeryEarly[1], 10);

            if (inPunchVeryEarlyHours > inPunchEarlyHours) return false;
            if (inPunchVeryEarlyHours === 24 && inPunchEarlyHours === 24) return false;
            if (inPunchEarlyHours === 24 && inPunchEarlyMinutes === 0) return false;
            if (inPunchVeryEarlyHours === inPunchEarlyHours && inPunchVeryEarlyMinutes > inPunchEarlyMinutes) return false;

            return true;
        }

        return false;
    };

    const validateOutPunchVeryLate = (value: string) => {
        if (value.length === 5 && values.out_punch_very_late) {
            const outPunchLate = value.split(':');
            const outPunchVeryLate = values.out_punch_very_late.split(':');

            const outPunchLateHours = parseInt(outPunchLate[0], 10);
            const outPunchLateMinutes = parseInt(outPunchLate[1], 10);
            const outPunchVeryLateHours = parseInt(outPunchVeryLate[0], 10);
            const outPunchVeryLateMinutes = parseInt(outPunchVeryLate[1], 10);

            if (outPunchVeryLateHours > outPunchLateHours) return false;
            if (outPunchVeryLateHours === 24 && outPunchLateHours === 24) return false;
            if (outPunchLateHours === 24 && outPunchLateMinutes === 0) return false;
            if (outPunchVeryLateHours === outPunchLateHours && outPunchVeryLateMinutes > outPunchLateMinutes) return false;

            return true;
        }

        return false;
    };

    const timeValidation = (value: string) => value.length > 0 && value.length < 5;

    const hasInvalidTimeFormat = [
        values.long_interval,
        values.shortened_shift,
        values.in_punch_very_early,
        values.in_punch_early,
        values.in_punch_late,
        values.out_punch_early,
        values.out_punch_late,
        values.out_punch_very_late,
    ].some((timeValue) => timeValidation(timeValue));

    const submitDisabled = useMemo(
        () =>
            values.name === '' ||
            hasInvalidTimeFormat ||
            validateInPunchVeryEarly(values.in_punch_early) ||
            validateOutPunchVeryLate(values.out_punch_late) ||
            existName(values.name),
        [values, rows, selectedRow, hasInvalidTimeFormat],
    );

    const changes = () => {
        const hasChanges =
            values.name !== '' ||
            values.description !== '' ||
            values.not_planning ||
            values.long_interval !== '' ||
            values.shortened_shift !== '' ||
            values.in_punch_very_early !== '' ||
            values.in_punch_early !== '' ||
            values.in_punch_late !== '' ||
            values.out_punch_early !== '' ||
            values.out_punch_late !== '' ||
            values.out_punch_very_late !== '' ||
            inPaycodesDuration !== null ||
            outPaycodesDuration !== null;

        if (hasChanges) {
            setAlert(true);
            return;
        }

        closeForm();
    };

    const editException = async () => {
        try {
            setLoading(true);

            const body = {
                name: values.name,
                not_planning: values.not_planning,
                long_interval: values.long_interval,
                shortened_shift: String(values.shortened_shift),
                in_punch_very_early: String(values.in_punch_very_early),
                in_punch_early: String(values.in_punch_early),
                in_punch_late: String(values.in_punch_late),
                out_punch_early: String(values.out_punch_early),
                out_punch_late: String(values.out_punch_late),
                out_punch_very_late: String(values.out_punch_very_late),
                in_paycode_id: inPaycodesDuration?.paycode_id || null,
                out_paycode_id: outPaycodesDuration?.paycode_id || null,
                description: values.description,
                enable: true,
            };

            const response = await editExceptionRule(setLoading, Number(selectedRow[0]), body);

            if (response) {
                enqueueSnackbar(t('settings.exception_rules.table.dialog.edit.messages.success'), { variant: 'success' });
                setOpen(false);
                closeForm();
                reloading();
            } else {
                enqueueSnackbar(t('settings.exception_rules.table.dialog.edit.messages.error'), { variant: 'error' });
            }
        } catch (error) {
            console.error('Error:', error);
            enqueueSnackbar(t('settings.exception_rules.table.dialog.edit.messages.error'), { variant: 'error' });
        }
    };

    return (
        <ExceptionRuleFormDialog
            open={open}
            mode="edit"
            loading={loading}
            values={values}
            paycodeOptions={paycodes as PaycodeOption[]}
            inPaycodesDuration={inPaycodesDuration}
            outPaycodesDuration={outPaycodesDuration}
            nameExists={existName(values.name)}
            nameExistsTextKey="custom_reports.reports.table.dialog.add.exist_name"
            validateInPunchVeryEarly={validateInPunchVeryEarly}
            validateOutPunchVeryLate={validateOutPunchVeryLate}
            timeValidation={timeValidation}
            submitDisabled={submitDisabled}
            showUnsavedAlert={alert}
            onNameChange={(name) => setValues((prev) => ({ ...prev, name }))}
            onDescriptionChange={(description) => setValues((prev) => ({ ...prev, description }))}
            onNotPlanningChange={(checked) => setValues((prev) => ({ ...prev, not_planning: checked }))}
            onTimeFieldChange={(field, value) => setValues((prev) => ({ ...prev, [field]: value }))}
            onInPaycodeChange={setInPaycodesDuration}
            onOutPaycodeChange={setOutPaycodesDuration}
            onRequestClose={changes}
            onDismissUnsavedAlert={() => setAlert(false)}
            onDiscardChanges={closeForm}
            onSubmit={editException}
        />
    );
};

export { EditExceptionRule };
